import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import {
	Box,
	Button,
	CircularProgress,
	Link,
	MenuItem,
	TextField,
	Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import LoadingButton from "../../../design/wilya-loading-button";
import {
	resetPhoneLogin,
	resetPhoneLoginError,
	sendLoginCode,
	verifyLoginCode,
} from "../user-slice";
import { PHONE_COUNTRIES, PhoneCountry } from "../util/phone-countries";
import {
	formatPhoneNumberForDisplay,
	formatPhoneNumberInput,
	normalizePhoneNumber,
} from "../util/phone-number";
import OneTimeCodeInput, { ONE_TIME_CODE_LENGTH } from "./one-time-code-input";
import useCountdown from "./use-countdown";

// Wait before the code can be resent when the API does not say otherwise.
const DEFAULT_RESEND_SECONDS = 30;

const COUNTRY_LABEL_KEYS: Record<PhoneCountry, string> = {
	US: "user_country-us",
	CA: "user_country-ca",
	MX: "user_country-mx",
};

interface PhoneLoginProps {
	onUseEmail: () => void;
}

const buttonLabelStyle: React.CSSProperties = {
	fontFamily: "Roboto",
	fontWeight: 500,
	fontSize: "15px",
	lineHeight: "26px",
	letterSpacing: "0.46px",
	textTransform: "uppercase",
};

/**
 * Supervisor sign-in with a texted one-time code: phone number → code. On a
 * verified code the slice runs the same success handling as password login,
 * so the login page's routing takes over from there.
 */
const PhoneLogin = ({ onUseEmail }: PhoneLoginProps) => {
	const { t } = useTranslation();
	const dispatch = useDispatch<any>();

	const sendStatus = useSelector(
		(state: any) => state.user.phoneLogin.sendStatus
	);
	const verifyStatus = useSelector(
		(state: any) => state.user.phoneLogin.verifyStatus
	);
	const errorMessage = useSelector(
		(state: any) => state.user.phoneLogin.errorMessage
	);
	const retryAfterSeconds = useSelector(
		(state: any) => state.user.phoneLogin.retryAfterSeconds
	);

	const [country, setCountry] = useState<PhoneCountry>("US");
	const [phoneInput, setPhoneInput] = useState<string>("");
	const [phoneError, setPhoneError] = useState<string>("");
	const [phoneNumber, setPhoneNumber] = useState<string>("");
	const [code, setCode] = useState<string>("");
	const [secondsLeft, startCountdown] = useCountdown();

	const isCodeStep = phoneNumber !== "";
	const isSending = sendStatus === "pending";
	const isVerifying = verifyStatus === "pending" || verifyStatus === "fulfilled";

	useEffect(() => {
		dispatch(resetPhoneLogin());
		return () => {
			dispatch(resetPhoneLogin());
		};
	}, [dispatch]);

	// Honour the API's throttle window (429 retryAfterSeconds).
	useEffect(() => {
		if (retryAfterSeconds > 0) startCountdown(retryAfterSeconds);
	}, [retryAfterSeconds, startCountdown]);

	const sendCode = useCallback(
		async (e164: string) => {
			const result = await dispatch(sendLoginCode({ phoneNumber: e164 }));
			if (sendLoginCode.fulfilled.match(result)) {
				setPhoneNumber(e164);
				setCode("");
				startCountdown(DEFAULT_RESEND_SECONDS);
			}
		},
		[dispatch, startCountdown]
	);

	const handleCountryChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const nextCountry = event.target.value as PhoneCountry;
			setCountry(nextCountry);
			setPhoneInput((previous) => formatPhoneNumberInput(previous, nextCountry));
			setPhoneError("");
		},
		[]
	);

	const handlePhoneChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const { value } = event.target;
			// Let deletions through untouched so backspacing over ")" or "-" works.
			setPhoneInput(
				value.length < phoneInput.length
					? value
					: formatPhoneNumberInput(value, country)
			);
			setPhoneError("");
			dispatch(resetPhoneLoginError());
		},
		[country, dispatch, phoneInput.length]
	);

	const handlePhoneSubmit = useCallback(
		(event: React.FormEvent<HTMLFormElement>) => {
			event.preventDefault();
			const e164 = normalizePhoneNumber(phoneInput, country);
			if (!e164) {
				setPhoneError(t("user_mobile-number-invalid"));
				return;
			}
			sendCode(e164);
		},
		[country, phoneInput, sendCode, t]
	);

	const verify = useCallback(
		async (value: string) => {
			if (value.length !== ONE_TIME_CODE_LENGTH) return;
			const result = await dispatch(
				verifyLoginCode({ phoneNumber, code: value })
			);
			if (verifyLoginCode.rejected.match(result)) setCode("");
		},
		[dispatch, phoneNumber]
	);

	const handleCodeChange = useCallback(
		(value: string) => {
			setCode(value);
			if (errorMessage) dispatch(resetPhoneLoginError());
		},
		[dispatch, errorMessage]
	);

	const handleCodeSubmit = useCallback(
		(event: React.FormEvent<HTMLFormElement>) => {
			event.preventDefault();
			verify(code);
		},
		[code, verify]
	);

	const handleResend = useCallback(() => {
		sendCode(phoneNumber);
	}, [phoneNumber, sendCode]);

	const handleChangeNumber = useCallback(() => {
		setPhoneNumber("");
		setCode("");
		dispatch(resetPhoneLogin());
	}, [dispatch]);

	const errorBox = errorMessage ? (
		<Box
			role='alert'
			mt={2}
			sx={{
				display: "flex",
				padding: "12px",
				flexDirection: "row",
				backgroundColor: "#FBEAEA",
				borderRadius: "4px",
			}}
		>
			<ErrorOutlineOutlinedIcon sx={{ color: "#E44B48" }} />
			<Box sx={{ marginLeft: "12px", marginTop: "2px" }}>
				<Typography
					sx={{
						fontSize: "16px",
						fontFamily: "Roboto",
						fontWeight: 500,
						color: "#541313",
					}}
				>
					{t("user_login-failed")}
				</Typography>
				<Typography
					sx={{
						fontFamily: "Roboto",
						fontWeight: 400,
						fontSize: "14px",
						color: "#541313",
					}}
				>
					{errorMessage}
				</Typography>
			</Box>
		</Box>
	) : null;

	const progressIndicator = (
		<CircularProgress size={16} sx={{ color: "rgba(0, 0, 0, 0.38)" }} />
	);

	if (!isCodeStep) {
		return (
			<Box component='form' noValidate onSubmit={handlePhoneSubmit} width='100%'>
				<TextField
					select
					id='phone-country'
					label={t("user_phone-country")}
					value={country}
					onChange={handleCountryChange}
					fullWidth
					variant='outlined'
					margin='normal'
					InputLabelProps={{ shrink: true }}
				>
					{PHONE_COUNTRIES.map((option) => (
						<MenuItem key={option.code} value={option.code}>
							{t(COUNTRY_LABEL_KEYS[option.code])}
						</MenuItem>
					))}
				</TextField>
				<TextField
					id='phone-number'
					label={t("user_mobile-number")}
					value={phoneInput}
					onChange={handlePhoneChange}
					error={phoneError !== ""}
					helperText={phoneError || t("user_mobile-number-hint")}
					autoFocus
					fullWidth
					variant='outlined'
					margin='normal'
					type='tel'
					InputLabelProps={{ shrink: true }}
					inputProps={{ autoComplete: "tel-national", inputMode: "tel" }}
				/>
				{errorBox}
				<Box pt={4}>
					<LoadingButton
						type='submit'
						fullWidth
						variant='contained'
						color='primary'
						size='large'
						disabled={phoneInput === "" || isSending || secondsLeft > 0}
						loading={isSending}
						loadingPosition='start'
						loadingIndicator={progressIndicator}
						startIcon={<></>}
					>
						<span style={buttonLabelStyle}>
							{secondsLeft > 0
								? t("user_resend-code-in", { seconds: secondsLeft })
								: t("user_send-code")}
						</span>
					</LoadingButton>
				</Box>
				<Box mt={4} textAlign='center'>
					<Link
						component='button'
						type='button'
						variant='body2'
						onClick={onUseEmail}
						sx={{ fontSize: "16px", color: "#2F4D8B" }}
					>
						{t("user_sign-in-with-email")}
					</Link>
				</Box>
			</Box>
		);
	}

	return (
		<Box component='form' noValidate onSubmit={handleCodeSubmit} width='100%'>
			<Typography variant='body1' sx={{ color: "#202020", mt: 1 }}>
				{t("user_code-sent-to", {
					phoneNumber: formatPhoneNumberForDisplay(phoneNumber),
				})}
			</Typography>
			<OneTimeCodeInput
				value={code}
				onChange={handleCodeChange}
				onComplete={verify}
				label={t("user_verification-code")}
				disabled={isVerifying}
				error={errorMessage !== ""}
				autoFocus
			/>
			{errorBox}
			<Box pt={4}>
				<LoadingButton
					type='submit'
					fullWidth
					variant='contained'
					color='primary'
					size='large'
					disabled={code.length !== ONE_TIME_CODE_LENGTH || isVerifying}
					loading={isVerifying}
					loadingPosition='start'
					loadingIndicator={progressIndicator}
					startIcon={<></>}
				>
					<span style={buttonLabelStyle}>
						{isVerifying ? t("user_loading") : t("user_verify-code")}
					</span>
				</LoadingButton>
			</Box>
			<Box
				mt={3}
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
				}}
			>
				<Button
					variant='text'
					onClick={handleChangeNumber}
					disabled={isVerifying}
					sx={{ textTransform: "none" }}
				>
					{t("user_change-mobile-number")}
				</Button>
				<Button
					variant='text'
					onClick={handleResend}
					disabled={secondsLeft > 0 || isSending || isVerifying}
					sx={{ textTransform: "none" }}
				>
					{secondsLeft > 0
						? t("user_resend-code-in", { seconds: secondsLeft })
						: t("user_resend-code")}
				</Button>
			</Box>
		</Box>
	);
};

export default PhoneLogin;
